// src/pages/YoungGeniusForm.jsx
import React, { useState } from 'react';

// --- Style Component (Custom CSS) ---
const PageCss = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    .step-section {
        display: none;
        opacity: 0;
        transition: opacity 0.5s ease-in-out;
    }
    .step-section.active {
        display: block;
        opacity: 1;
    }
    #share-status {
        position: fixed; bottom: 20px; left: 50%;
        transform: translateX(-50%); background-color: rgba(0, 0, 0, 0.7);
        color: white; padding: 10px 20px; border-radius: 8px;
        opacity: 0; transition: opacity 0.3s ease-in-out; z-index: 50;
    }
    #share-status.show { opacity: 1; }
    /* Tailwind input focus rings */
    select:focus, input:focus, textarea:focus {
        --tw-ring-color: #a855f7; /* ring-purple-500 */
        --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
        --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
        box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
        border-color: #a855f7; /* border-purple-500 */
    }
    /* Quiz Answer Feedback */
     input.correct-answer {
        border-color: #10b981; /* border-green-500 */
        box-shadow: 0 0 0 2px #34d399; /* ring-2 ring-green-500 */
    }
    input.incorrect-answer {
        border-color: #ef4444; /* border-red-500 */
        box-shadow: 0 0 0 2px #f87171; /* ring-2 ring-red-500 */
    }
  `}} />
);

// --- Main Component ---
function YoungGeniusForm() {
    const [currentStep, setCurrentStep] = useState('subject');
    const [formData, setFormData] = useState({
        age: '',
        subject: '',
        customSubject: '',
        name: '',
        email: '',
        phone: '',
        goal: '',
        customGoal: '',
    });
    const [quizAnswers, setQuizAnswers] = useState({ q1: '', q2: '', q3: '' });
    const [quizFeedback, setQuizFeedback] = useState({ q1: null, q2: null, q3: null });
    const [shareStatus, setShareStatus] = useState({ show: false, message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const quizCorrectAnswers = { q1: '9', q2: 'mitochondria', q3: 'william shakespeare' };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));

        if (id === 'subject' && value !== 'Others') {
            setFormData(prev => ({ ...prev, customSubject: '' }));
        }
        if (id === 'goal' && value !== 'Other') {
            setFormData(prev => ({ ...prev, customGoal: '' }));
        }
    };

    const handleQuizInputChange = (e) => {
        const { id, value } = e.target;
        setQuizAnswers(prev => ({ ...prev, [id]: value }));
        setQuizFeedback(prev => ({ ...prev, [id]: null }));
    };

    const showShareMessage = (message) => {
        setShareStatus({ show: true, message: message });
        setTimeout(() => {
            setShareStatus({ show: false, message: '' });
        }, 3000);
    };

    const handleSubjectNext = () => {
        if (!formData.age || !formData.subject) {
            showShareMessage("Please select an age and a subject.");
            return;
        }
        if (formData.subject === 'Others' && !formData.customSubject.trim()) {
            showShareMessage("Please enter a custom subject name.");
            return;
        }
        setCurrentStep('quiz');
    };

    const handleQuizSubmit = (e) => {
        e.preventDefault();
        const feedback = {};
        let allCorrect = true;
        Object.keys(quizCorrectAnswers).forEach(key => {
            const isCorrect = quizAnswers[key].trim().toLowerCase() === quizCorrectAnswers[key];
            feedback[key] = isCorrect ? 'correct' : 'incorrect';
            if (!isCorrect) allCorrect = false;
        });
        setQuizFeedback(feedback);
        setCurrentStep('form');
    };

    const handleUserInfoSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            setIsSubmitting(false);
            setCurrentStep('final'); 
        }, 1500);
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href)
            .then(() => showShareMessage("Link copied to clipboard!"))
            .catch(err => console.error('Failed to copy: ', err));
    };

    // Styles applied directly to component
    const pageStyles = {
        fontFamily: "'Inter', sans-serif",
        backgroundImage: "url('/assets/backgrounds/custom/young_genius_start_bkgr.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        width: '100%',
        minHeight: '100vh'
    };

    return (
        <div className="flex flex-col items-center min-h-screen p-4 md:p-8" style={pageStyles}>
            <PageCss />

            {/* Navigation Header */}
            <header className="w-full max-w-5xl mb-8 flex justify-between items-center bg-white p-4 rounded-full shadow-lg">
                 <div className="flex-shrink-0">
                    <a href="/" className="text-xl font-bold text-purple-600 hover:text-purple-800 transition duration-300">
                        Aulice Academy
                    </a>
                 </div>
                 <div className="flex items-center space-x-2">
                    <button id="share-button" onClick={handleShare} className="bg-purple-600 text-white font-semibold py-2 px-4 rounded-full shadow-md hover:bg-purple-700 transition duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500">
                        Share
                    </button>
                </div>
            </header>

            {/* Main content container */}
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl max-w-xl w-full">

                {/* Step 1: Subject and Age Selection */}
                <div id="subject-section" className={`step-section ${currentStep === 'subject' ? 'active' : ''}`}>
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">Explore Your Genius!</h1>
                    <p className="text-center text-gray-500 mb-6">Select your age and subject of interest to get started.</p>

                    <div className="mb-4">
                        <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">Your Age</label>
                        <select id="age" value={formData.age} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm transition duration-150">
                            <option value="" disabled>-- Select your age --</option>
                            {[...Array(15).keys()].map(i => i + 7).map(age => <option key={age} value={age}>{age}</option>)} {/* Ages 7-21 */}
                        </select>
                    </div>

                    <div className="mb-4">
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">School Subject</label>
                        <select id="subject" value={formData.subject} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm transition duration-150">
                            <option value="" disabled>-- Select a subject --</option>
                            {["Mathematics", "Sciences", "Physics", "Chemistry", "Biology", "Zoology", "Literature", "Arts", "Sports", "Others"].map(subj => <option key={subj} value={subj}>{subj}</option>)}
                        </select>
                    </div>

                    <div id="custom-subject-input" className={formData.subject !== 'Others' ? 'hidden' : ''}>
                        <label htmlFor="customSubject" className="block text-sm font-medium text-gray-700 mb-2">Custom Subject</label>
                        <input type="text" id="customSubject" placeholder="e.g., Robotics" value={formData.customSubject} onChange={handleInputChange} required={formData.subject === 'Others'} className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm transition duration-150" />
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 mt-6">
                        <button id="subject-next-btn" onClick={handleSubjectNext} className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition duration-300">
                            Continue
                        </button>
                    </div>
                </div>

                {/* Step 2: Quick Quiz */}
                <div id="quiz-section" className={`step-section ${currentStep === 'quiz' ? 'active' : ''}`}>
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">Quick School Subject Quiz</h1>
                    <p className="text-center text-gray-500 mb-6">Test your knowledge with these questions.</p>

                    <form id="quiz-form" onSubmit={handleQuizSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="q1" className="block text-sm font-medium text-gray-700 mb-2">1. What is the square root of 81?</label>
                            <input type="text" id="q1" value={quizAnswers.q1} onChange={handleQuizInputChange} required className={`w-full p-3 border border-gray-300 rounded-lg shadow-sm ${quizFeedback.q1 ? (quizFeedback.q1 === 'correct' ? 'correct-answer' : 'incorrect-answer') : ''}`} />
                        </div>
                        <div>
                            <label htmlFor="q2" className="block text-sm font-medium text-gray-700 mb-2">2. What is the powerhouse of the cell?</label>
                            <input type="text" id="q2" value={quizAnswers.q2} onChange={handleQuizInputChange} required className={`w-full p-3 border border-gray-300 rounded-lg shadow-sm ${quizFeedback.q2 ? (quizFeedback.q2 === 'correct' ? 'correct-answer' : 'incorrect-answer') : ''}`} />
                        </div>
                        <div>
                            <label htmlFor="q3" className="block text-sm font-medium text-gray-700 mb-2">3. Who wrote 'Romeo and Juliet'?</label>
                            <input type="text" id="q3" value={quizAnswers.q3} onChange={handleQuizInputChange} required className={`w-full p-3 border border-gray-300 rounded-lg shadow-sm ${quizFeedback.q3 ? (quizFeedback.q3 === 'correct' ? 'correct-answer' : 'incorrect-answer') : ''}`} />
                        </div>

                        <button type="submit" className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition duration-300">
                            Check My Answers & Continue
                        </button>
                    </form>
                </div>

                {/* Step 3: Congratulations & User Form */}
                <div id="form-section" className={`step-section ${currentStep === 'form' ? 'active' : ''}`}>
                    <h1 className="text-3xl font-bold text-center text-green-600 mb-4">Congratulations!</h1>
                    <p className="text-center text-lg text-gray-700 mb-8">You've won a **free consultation** to design your tailor-made on-demand course.</p>

                    <form id="user-info-form" onSubmit={handleUserInfoSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input type="text" id="name" value={formData.name} onChange={handleInputChange} required className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                            <input type="email" id="email" value={formData.email} onChange={handleInputChange} required className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                            <input type="tel" id="phone" value={formData.phone} onChange={handleInputChange} required className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm" />
                        </div>
                        <div>
                             <label htmlFor="goal" className="block text-sm font-medium text-gray-700">Your Goal</label>
                            <select id="goal" value={formData.goal} onChange={handleInputChange} required className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm">
                                <option value="" disabled>-- Select a goal --</option>
                                {["Employment", "Promotion", "Relocation", "Exam", "Education", "Conference", "Project", "Business", "Other"].map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </div>
                        <div id="custom-goal-input" className={formData.goal !== 'Other' ? 'hidden' : ''}>
                            <label htmlFor="customGoal" className="block text-sm font-medium text-gray-700">Describe your goal</label>
                            <textarea id="customGoal" rows="3" value={formData.customGoal} onChange={handleInputChange} required={formData.goal === 'Other'} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm"></textarea>
                        </div>
                        <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition duration-300 disabled:opacity-50">
                            {isSubmitting ? 'Submitting...' : 'Submit & Schedule'}
                        </button>
                    </form>
                </div>

                {/* Step 4: Final Screen */}
                <div id="final-section" className={`step-section text-center ${currentStep === 'final' ? 'active' : ''}`}>
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">You're All Set!</h1>
                    <p className="text-center text-lg text-gray-600 mb-8">Click the button below to schedule your free consultation with one of our experts and start designing your course.</p>

                    <a href="https://calendly.com/aulice" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-purple-600 text-white font-semibold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105">
                        Schedule My Free Session 🗓️
                    </a>
                </div>

            </div> 

            {/* Share status message */}
            <div id="share-status" className={`${shareStatus.show ? 'show' : 'hidden'}`}>
                {shareStatus.message}
            </div>

        </div> 
    );
}

export default YoungGeniusForm;